import type { EnvValueType, EnvVisibility, Platform } from '@hangar/shared';
import type { EnvVar, Warning } from '@/api/schemas';

/** Scope picker values (SPEC §12.1 precedence: org < project < project+env < app < app+env < app+env+platform). */
export type ScopeKey = 'org' | 'project' | 'project-env' | 'app' | 'app-env' | 'platform';

export const SCOPE_OPTIONS: Array<{ value: ScopeKey; label: string; hint: string }> = [
  { value: 'org', label: 'Organization', hint: 'Every project inherits these' },
  { value: 'project', label: 'Project', hint: 'All environments of the project' },
  { value: 'project-env', label: 'Project + environment', hint: 'One environment, every app' },
  { value: 'app', label: 'App', hint: 'All environments of one app' },
  { value: 'app-env', label: 'App + environment', hint: 'One app in one environment' },
  { value: 'platform', label: 'App + environment + platform', hint: 'Highest precedence before trigger overrides' },
];

export const needsApp = (s: ScopeKey) => s === 'app' || s === 'app-env' || s === 'platform';
export const needsEnvironment = (s: ScopeKey) => s === 'project-env' || s === 'app-env' || s === 'platform';
export const needsPlatform = (s: ScopeKey) => s === 'platform';

export const VISIBILITY_HELP: Record<EnvVisibility, string> = {
  plain: 'Readable by any member (URLs, flags).',
  sensitive: 'Masked; reveal needs env:reveal and is audited.',
  secret: 'Write-only: never returned by the API, the console or exports. Reaches builds only through the runtime token.',
};

export const VALUE_TYPE_HELP: Record<EnvValueType, string> = {
  string: 'One line.',
  multiline: 'Real line breaks are kept (licenses, PEM keys).',
  file: 'Materialized as a file at build time (google-services.json).',
  json: 'Validated as JSON on save.',
};

export const isExpoPublic = (key: string) => key.startsWith('EXPO_PUBLIC_');
export const LITERAL_NEWLINE = /\\n/;

/** Client-side detection of the Scanbot bug: a two-character `\n` sequence where a real newline was probably intended. */
export function literalNewlineCount(value: string): number {
  return (value.match(/\\n/g) ?? []).length;
}
export const convertLiteralNewlines = (value: string) => value.replace(/\\n/g, '\n');

const warn = (v: EnvVar, code: string, severity: Warning['severity'], message: string): Warning => ({ code, severity, message, key: v.key });

/** `EXPO_PUBLIC_*` keys are inlined into the JS bundle: secret is forbidden, sensitive only masks the console. */
function expoPublicWarnings(v: EnvVar): Warning[] {
  if (!isExpoPublic(v.key)) return [];
  if (v.visibility === 'secret') return [warn(v, 'EXPO_PUBLIC_SECRET_FORBIDDEN', 'error', 'EXPO_PUBLIC_* values are inlined into the JS bundle and cannot be secret.')];
  if (v.visibility === 'sensitive') return [warn(v, 'EXPO_PUBLIC_INLINED', 'info', 'Inlined into the JS bundle at build time — sensitive only masks it in the console.')];
  return [];
}

function lineWarnings(v: EnvVar, cv: NonNullable<EnvVar['currentVersion']>): Warning[] {
  const out: Warning[] = [];
  if (v.valueType === 'multiline' && cv.lineCount <= 1 && cv.byteLength > 0) out.push(warn(v, 'LITERAL_BACKSLASH_N', 'warning', 'Declared multiline but the stored value has a single line — it may contain a literal "\\n" instead of real line breaks.'));
  if (v.valueType === 'string' && cv.lineCount > 1) out.push(warn(v, 'MULTILINE_DETECTED', 'warning', `The value has ${cv.lineCount} lines but the type is string; switch it to multiline.`));
  return out;
}

function versionWarnings(v: EnvVar): Warning[] {
  const cv = v.currentVersion;
  if (!cv) return [warn(v, 'NO_VERSION', 'warning', 'No current version.')];
  const out = lineWarnings(v, cv);
  if (cv.byteLength === 0) out.push(warn(v, 'EMPTY_VALUE', 'warning', 'The current version is empty.'));
  if (cv.pendingApprovalId) out.push(warn(v, 'PENDING_APPROVAL', 'info', 'The latest version is waiting for approval and is not used by builds yet.'));
  return out;
}

/** Findings the console can derive from metadata alone (values never reach the browser). */
export function derivedWarnings(v: EnvVar): Warning[] {
  return [...expoPublicWarnings(v), ...versionWarnings(v)];
}

/** "1 line" / "3 lines", or null when the item has no line count. */
export function lineCountLabel(lineCount: number | null | undefined): string | null {
  if (lineCount === null || lineCount === undefined) return null;
  return `${lineCount} line${lineCount === 1 ? '' : 's'}`;
}

export type ListView = 'loading' | 'error' | 'empty' | 'list';

/** Which body a fetched list shows: loading / error only while there is no data yet; otherwise empty or the rows. */
export function listView(i: { isLoading: boolean; isError: boolean; hasData: boolean; count: number }): ListView {
  if (i.isLoading && !i.hasData) return 'loading';
  if (i.isError && !i.hasData) return 'error';
  return i.count === 0 ? 'empty' : 'list';
}

export type EnvListState = 'pick-scope' | 'loading' | 'error' | 'empty' | 'list';

/** Which body the variables list shows: the scope must be picked first, then loading / error (only while there is no data yet) / empty / rows. */
export function envListState(i: { scopeReady: boolean; isLoading: boolean; isError: boolean; vars: EnvVar[] | undefined }): EnvListState {
  if (!i.scopeReady) return 'pick-scope';
  if (i.isLoading && !i.vars) return 'loading';
  if (i.isError && !i.vars) return 'error';
  return !i.vars || i.vars.length === 0 ? 'empty' : 'list';
}

export const PLATFORMS: Platform[] = ['android', 'ios'];

/** Base64 of a File (for `valueBase64` uploads). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}
